import { useState } from 'react';
import { useNavigate } from 'react-router';
import { paths } from '../../../common/constants/pathVariables.js';
import { callUiApi } from '../../../common/fetch.js';
import { finalizeCardPaymentPrivateMethod } from '../../../generated/api/uiApiMethods.js';
import type { FinalizeCardPaymentRequest } from '../../../generated/api/finalizeCardPaymentRequest.js';

type FinalizeStatus = 'idle' | 'loading' | 'success' | 'error';

interface UseFinalizeCardPaymentReturn {
  finalizeStatus: FinalizeStatus;
  finalizeCardPayment: (request: FinalizeCardPaymentRequest) => Promise<void>;
}

/**
 * Hook to finalize card payment after returning from Nets.
 *
 * Calls POST /api/ui/v3/private/customer-admin/orders/finalize-card-payment
 * - 201/202: Success → navigates to thank you page (analytics tracked there)
 * - Other: Error → sets status to 'error'
 */
export const useFinalizeCardPayment = (): UseFinalizeCardPaymentReturn => {
  const navigate = useNavigate();
  const [finalizeStatus, setFinalizeStatus] = useState<FinalizeStatus>('idle');

  const finalizeCardPayment = async (request: FinalizeCardPaymentRequest): Promise<void> => {
    setFinalizeStatus('loading');

    try {
      const response = await callUiApi(finalizeCardPaymentPrivateMethod(), {
        body: JSON.stringify(request),
      });

      if (response.status === 201 || response.status === 202) {
        // Success - navigate to thank you page (analytics tracked there)
        setFinalizeStatus('success');
        navigate(paths.PLACE_ORDER_THANK_YOU);
      } else {
        // Error response
        setFinalizeStatus('error');
      }
    } catch {
      // Network or other error
      setFinalizeStatus('error');
    }
  };

  return { finalizeStatus, finalizeCardPayment };
};
